"use client";

import { Bot, SquarePen } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { mediaTypeIcon, type ThreadListItem } from "@/lib/widget-thread";

/**
 * Conversation list for the Messages tab: one row per topic
 * thread (avatar · name · last-message preview · relative time · unread dot),
 * with a prominent "ask a question" button that starts a NEW thread.
 *
 * Presentational — the caller supplies `threads` (mock today via
 * `getMockThreads`, live `visitorThreads` via a thin adapter once the BE
 * contract lands) and the two navigation callbacks. Tapping a row opens that
 * thread's conversation; the button mints a fresh thread.
 */
export function ThreadList({
  threads,
  loading = false,
  loadError = false,
  onRetry,
  avatarUrl,
  onOpenThread,
  onNewQuestion,
  blocked = false,
  slotThread = null,
}: {
  /** A synthetic row for the page's top launcher MESSAGE slot, shown only while the visitor has no
   *  thread (the slot's text is the preview, the sender heads the row). */
  slotThread?: ThreadListItem | null;
  threads: ThreadListItem[];
  /** True while the first `visitorThreads` fetch is in flight with nothing
   *  cached yet — show a skeleton instead of flashing the empty state. */
  loading?: boolean;
  /** The `visitorThreads` fetch FAILED with nothing loaded (offline / BE down).
   *  Threads are private (never cached), so we can't fall back to a cache —
   *  show a "couldn't load" state instead of the misleading empty state. */
  loadError?: boolean;
  /** Retry the thread-list fetch (wired to `visitorThreads` refetch). */
  onRetry?: () => void;
  /** Fallback row avatar (the operator/AI assistant logo) when a thread has
   *  no per-sender avatar — every thread is with the same team. */
  avatarUrl?: string;
  onOpenThread: (thread: ThreadListItem) => void;
  onNewQuestion: () => void;
  /** The visitor is GLOBALLY blocked — the "ask a question" CTA is replaced by
   *  a blocked notice (no new conversations). */
  blocked?: boolean;
}) {
  const t = useTranslations("messages");
  const locale = useLocale();

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        {loading && threads.length === 0 ? (
          <ThreadListSkeleton />
        ) : loadError && threads.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <p className="font-semibold text-base text-wx-fg">{t("threadsErrorTitle")}</p>
            <p className="text-sm text-wx-fg-muted">{t("threadsErrorBody")}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-1 font-medium text-sm text-wx-primary underline-offset-2 hover:underline"
              >
                {t("retry")}
              </button>
            )}
          </div>
        ) : threads.length === 0 && slotThread ? (
          // No thread yet: the page's launcher slot stands in as the one row (its thread is created
          // on the first send).
          <ul data-wx-thread-list="" className="flex flex-col pb-[4.5rem]">
            <ThreadRow
              thread={slotThread}
              avatarUrl={avatarUrl}
              locale={locale}
              onClick={() => onOpenThread(slotThread)}
            />
          </ul>
        ) : threads.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <p className="font-semibold text-base text-wx-fg">{t("threadsEmptyTitle")}</p>
            <p className="text-sm text-wx-fg-muted">{t("threadsEmptyBody")}</p>
          </div>
        ) : (
          <ul data-wx-thread-list="" className="flex flex-col pb-[4.5rem]">
            {threads.map((thread) => (
              <ThreadRow
                key={thread.chatId || thread.threadKey || thread.updatedAt}
                thread={thread}
                avatarUrl={avatarUrl}
                locale={locale}
                onClick={() => onOpenThread(thread)}
              />
            ))}
          </ul>
        )}
      </ScrollArea>

      {/* "Ask a question" — a compact centered pill FLOATING over the bottom of the list (which scrolls
          the full height behind it and keeps a bottom inset), with a theme-colored fade underneath.
          Starts a new thread. Replaced by a blocked notice when the visitor is globally blocked (no
          new conversations). The wrapper ignores pointer events so the fade never blocks a row. */}
      <div
        data-wx-thread-cta=""
        className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-wx-bg via-wx-bg/80 to-transparent px-4 pt-6 pb-3"
      >
        {blocked ? (
          <p className="pointer-events-auto px-4 py-2 text-center text-[13px] text-wx-fg-muted">
            {t("visitorBlocked")}
          </p>
        ) : (
          <button
            type="button"
            onClick={onNewQuestion}
            className={cn(
              "pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2",
              "bg-wx-primary font-semibold text-[13px] text-wx-primary-fg shadow-sm",
              "transition-transform duration-150 ease-out hover:scale-[1.04] active:scale-95",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary focus-visible:ring-offset-2 focus-visible:ring-offset-wx-bg",
            )}
          >
            <SquarePen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t("askQuestion")}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function ThreadRow({
  thread,
  avatarUrl,
  locale,
  onClick,
}: {
  thread: ThreadListItem;
  avatarUrl?: string;
  locale: string;
  onClick: () => void;
}) {
  const t = useTranslations("messages");
  const tHome = useTranslations("home");
  const unread = thread.unreadCount > 0;
  const typing = !!thread.typing;
  const connected = thread.assignedStatus === "connected";
  // Row title = who sent the LAST message. The query's `lastSenderKind` is
  // authoritative: "operator" → their name; any other KNOWN kind (ai/bot/
  // system) → the AI persona, even if an operator is ASSIGNED (an assigned
  // thread whose last reply was the AI must still read as the AI, not the
  // operator). Only when we have no last-sender info (null — e.g. a live SSE
  // update that doesn't carry it) do we fall back to the assignment signal.
  const rowTitle =
    thread.lastSenderKind === "operator" && thread.lastSenderName
      ? thread.lastSenderName
      : thread.lastSenderKind && thread.lastSenderKind !== "visitor"
        ? thread.senderName
        : connected && thread.assignedOperatorName
          ? thread.assignedOperatorName
          : thread.senderName;
  // `thread.title` (the slot text) is for the operator inbox; the visitor list headlines the sender.
  // A media last message shows a type glyph + caption (or a localized
  // "Attachment" when there's no caption) instead of a blank line.
  const previewBody = thread.preview?.media
    ? `${mediaTypeIcon(thread.preview.mediaType)} ${thread.preview.text || tHome("recentMessageAttachment")}`
    : (thread.preview?.text ?? "");
  const previewText = thread.preview
    ? thread.preview.fromVisitor
      ? t("previewYou", { text: previewBody })
      : previewBody
    : "";
  const time = thread.preview?.at ?? thread.updatedAt;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full items-start gap-3 border-wx-border border-b px-4 py-3.5 text-left",
          "transition-colors last:border-b-0 hover:bg-wx-bg-elevated-2",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-wx-primary",
        )}
      >
        <Avatar src={thread.senderAvatar ?? avatarUrl} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1.5 truncate text-sm text-wx-fg",
                unread ? "font-semibold" : "font-medium",
              )}
            >
              {connected && (
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-wx-success"
                  aria-hidden="true"
                />
              )}
              <span className="truncate">{rowTitle}</span>
            </span>
            <span className="shrink-0 text-[11px] text-wx-fg-subtle">
              {relativeTime(time, locale)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {typing ? (
              <span className="min-w-0 flex-1 truncate text-[13px] text-wx-primary italic">
                {t("typing")}
              </span>
            ) : (
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-[13px]",
                  unread ? "text-wx-fg" : "text-wx-fg-muted",
                )}
              >
                {previewText}
              </span>
            )}
            {unread && !typing && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-wx-primary" aria-hidden="true" />
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

/** Loading placeholder — a few ghost rows that match `ThreadRow`'s geometry,
 *  shown while the first thread fetch is in flight so the list never flashes
 *  its empty state before data arrives. */
function ThreadListSkeleton() {
  return (
    <ul className="flex flex-col" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-start gap-3 border-wx-border border-b px-4 py-3.5">
          <Skeleton className="mt-0.5 h-9 w-9 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Decorative row avatar — the thread's sender name is shown as text beside
 *  it, so the avatar itself is `aria-hidden`. */
function Avatar({ src }: { src?: string }) {
  if (src) {
    return (
      // biome-ignore lint/performance/noImgElement: operator/AI avatar of arbitrary host; next/image needs an allowlist
      <img src={src} alt="" className="mt-0.5 h-9 w-9 shrink-0 rounded-full object-cover" />
    );
  }
  return (
    <div
      className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wx-bg-elevated-2 text-wx-fg-muted"
      aria-hidden="true"
    >
      <Bot className="h-5 w-5" />
    </div>
  );
}

/** Localized compact relative time ("4m", "2h", "3d", "1w") for a thread row.
 *  Uses `Intl.RelativeTimeFormat` so it respects the visitor's locale without
 *  a hardcoded string. Inline for now; extract + test when the list is wired
 *  to the live BE. */
function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "narrow" });
  if (abs < 60) return rtf.format(Math.round(diffSec), "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  if (abs < 604800) return rtf.format(Math.round(diffSec / 86400), "day");
  return rtf.format(Math.round(diffSec / 604800), "week");
}
